import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import _isEmpty from 'lodash/isEmpty';
import loadable from '@loadable/component';

// Router
import { getRoute } from '../../entry/routes';

// State
import {
  resetCheckoutPayment,
  resetCheckoutField,
  fetchOrderList,
  setLocalStorageCheckout,
} from '../../state/modules/order/actions';
import { setBonuses } from '../../state/modules/user/actions';
import { clearStock } from '../../state/modules/stock/actions';
import { cancelBonusesUsage } from '../../state/modules/sailplay/actions';
import { fetchCards } from '../../state/modules/card/actions';
import { fetchUpsale } from '../../state/modules/catalog/actions';
import { fetchAddresses } from '../../state/modules/address/actions';
import { priceIncludingBonusesSelector } from '../../state/modules/order/selectors';
import { unusedBonusesSum as unusedBonusesSumSelector } from '../../state/modules/user/selectors';

// Utils
import isomorphicCookies from '../../utils/isomorphicCookies';

// Hooks
import useReapplyStock from './useReapplyStock';

const DesktopView = loadable(() => import('../../components/Checkout/DesktopView'));
const MobileView = loadable(() => import('../../components/Checkout/MobileView'));

export default function OrderPage({ step = 1 }) {
  const intl = useIntl();

  const { name, declension } = useSelector(state => state.city.userCity);
  const isRealPhone = useSelector(state => state.responsive.isRealPhone);

  const View = isRealPhone ? MobileView : DesktopView;

  return (
    <>
      <Helmet title={intl.formatMessage({ id: 'page.order.helmet' }, { declension, name })} />
      <OrderEffect />
      <View step={step} />
    </>
  );
}

function OrderEffect() {
  const history = useHistory();
  const dispatch = useDispatch();

  const { isLoaded, composition } = useSelector(state => state.cart);
  const stockCode = useSelector(state => state.stock.data.code);
  const checkout = useSelector(state => state.order.checkout);
  const isAuthenticated = useSelector(state => state.user.isAuthenticated);
  const priceIncludingBonuses = useSelector(priceIncludingBonusesSelector);
  const unusedBonusesSum = useSelector(unusedBonusesSumSelector);

  const { currentRestaurant, isPayed, order_data, order_id } = checkout;

  useReapplyStock(); // очень мого ререндеров

  useEffect(() => {
    return () => {
      dispatch(resetCheckoutField('error'));
    };
  }, []);

  useEffect(() => {
    // при каждом изменении чекаута записываем это в локалстораж
    if (currentRestaurant) {
      dispatch(setLocalStorageCheckout());
    }
  }, [checkout]);

  useEffect(() => {
    // запрашиваем список товаров в апсейл. переспрашиваем при каждом изменении стоимости корзины или смене промокода
    dispatch(fetchUpsale());
  }, [priceIncludingBonuses, stockCode]);

  useEffect(() => {
    if (isPayed) {
      // сбрасываем чекаут
      dispatch(resetCheckoutPayment());
      // сбрасываем промокод
      dispatch(clearStock());
      // надо отправить аналитику что заказ прошел
      dispatch({ type: '@analytic/ORDER_SENDED', payload: order_data });

      if (isAuthenticated) {
        // устанавливаем новое кол-во доступных бонусов
        dispatch(setBonuses(unusedBonusesSum));
        // сбрасываем использование бонусов из стейта
        dispatch(cancelBonusesUsage());
        // после завершения заказа должен сохраниться адрес
        dispatch(fetchAddresses());
        // подтягиваем список сохраненных карт, тк во время заказа мы могли ее сохранить
        dispatch(fetchCards());
        // подтягиваем список заказов
        dispatch(fetchOrderList());
      }

      isomorphicCookies().remove('thx_link_closed');
      // для отслеживания заказа у неавторизованного пользователя
      isomorphicCookies().set('last_order_id', order_id, { expires: 1 });
      // запуск минутного таймера отмены для определенного заказа
      isomorphicCookies().set('cancel_timer', `${order_id}|${new Date().getTime()}`, {
        expires: 1,
      });
      // Закидываем на thankyoupage
      history.push(`${getRoute('orderTrackingAlias', order_id)}`);
    }
  }, [isPayed]);

  // если корзина пустая, а заказ не оплачен, то выкидывать на главную
  useEffect(() => {
    if (isLoaded && _isEmpty(composition) && !isPayed) {
      if (isAuthenticated) {
        // сбрасываем использование бонусов из стейта
        dispatch(cancelBonusesUsage());
      }
      // выкидываем на главную
      history.push(getRoute('home'));
    }
  }, [isLoaded, composition, isPayed]);

  return null;
}
